import { Badge, Copy, Flex, Table, TableAction, Typography, useMessagedAsyncRetry } from "@payping/web-components";
import { TransactionFilter } from "@/components/DashReport/TransactionTabFilter";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useMemo, useState } from "react";
import { getAdvancedTransactions } from "@payping/redux/actions";
import { toJalali } from "@payping/utils/dateUtils";
import { getCorrectDate } from "@/utils/getCorrectDate";
import { ensureString } from "@payping/utilities-shared/string";
import { PaymentStatusEnum, PaymentTypeEnum, getTransactionStatus } from "@payping/api";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { ColumnsType } from "@payping/web-components/components/Table/types";
import { JALALI_DATE_TIME_FORMAT } from "@/shared/constants/date.constant";
import TransactionDetails from "../TransactionDetails";
import { CuToman } from "@payping/icons";
import { parseAsStringLiteral, useQueryStates } from "nuqs";
import { enumNumbers, searchParamsParser } from "@/core/utils/search-params-parser";

export interface TransactionRowType {
  amount: string;
  code: string;
  date: string;
  name: string;
  status: string;
  statusColor: string;
  clientRefId: string;
  // emphasis on cash transaction
  payMethod: string;
  ipgType?: number;
}

export const transactionFiltersSearchVariant = [
  "payerName",
  "payerPhoneNumber",
  "code",
  "refId",
  "clientRefId",
  "ipgRefId",
  "cardNumber",
  "searchByType",
] as const;

export interface TransactionsFilters {
  pageNumber?: number | null;
  pageSize?: number | null;
  amount?: number | null;
  paymentType?: PaymentTypeEnum | null;
  paymentStatus?: PaymentStatusEnum | null;
  clientId?: string | null;
  clientRefId?: string | null;
  code?: string | null;
  refId?: string | null;
  ipgRefId?: string | null;
  cardNumber?: string | null;
  payerName?: string | null;
  payerPhoneNumber?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  searchVariant?: (typeof transactionFiltersSearchVariant)[number] | null;
}

const ShowAllTransactionDetails = () => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const [detailCode, setDetailCode] = useState<string | undefined>(undefined);

  // search params
  const [searchParams, setSearchParams] = useQueryStates({
    clientId: searchParamsParser.parseAsString,
    amount: searchParamsParser.parseAsInteger,
    paymentType: searchParamsParser.parseAsInteger,
    paymentStatus: searchParamsParser.parseAsIntegerEnum<PaymentStatusEnum>(enumNumbers(PaymentStatusEnum)),
    pageNumber: searchParamsParser.parseAsInteger,
    pageSize: searchParamsParser.parseAsInteger,
    clientRefId: searchParamsParser.parseAsString,
    code: searchParamsParser.parseAsString,
    ipgRefId: searchParamsParser.parseAsString,
    refId: searchParamsParser.parseAsString,
    cardNumber: searchParamsParser.parseAsString,
    payerName: searchParamsParser.parseAsString,
    payerPhoneNumber: searchParamsParser.parseAsString,
    fromDate: searchParamsParser.parseAsString,
    toDate: searchParamsParser.parseAsString,
    searchVariant: parseAsStringLiteral(transactionFiltersSearchVariant),
  });

  const list = useAppSelector((state) => state.reports.list);
  const listCount = useAppSelector((state) => state.reports.totalCount);
  const transactionDetailsData = useMemo(() => list.find((item) => item.code === detailCode), [list, detailCode]);

  const pageSize = searchParams.pageSize ?? 10;
  const pageNumber = searchParams.pageNumber ?? 1;

  const { loading, retry } = useMessagedAsyncRetry(
    async () =>
      dispatch(
        getAdvancedTransactions({
          amount: searchParams.amount ?? undefined,
          cardNumber: searchParams.cardNumber ?? undefined,
          clientId: searchParams.clientId ?? undefined,
          clientRefId: searchParams.clientRefId ?? undefined,
          code: searchParams.code ?? undefined,
          fromDate: searchParams.fromDate ?? undefined,
          ipgRefId: searchParams.ipgRefId ?? undefined,
          limit: pageSize,
          offset: (pageNumber - 1) * pageSize,
          payerName: searchParams.payerName ?? undefined,
          payerPhoneNumber: searchParams.payerPhoneNumber ?? undefined,
          paymentStatus: searchParams.paymentStatus ?? PaymentStatusEnum.IsPaid,
          paymentType: searchParams.paymentType ?? undefined,
          refId: searchParams.refId ?? undefined,
          toDate: searchParams.toDate ?? undefined,
        }),
      ),
    [searchParams],
    { success: false, useProgress: true },
  );

  const data = list.map((item, index) => {
    const {
      name,
      code: listItemCode,
      createdDate,
      amount: listItemAmount,
      payDate,
      ipgType,
      isRefund,
      clientRefId,
    } = item;

    const date = toJalali({
      date: getCorrectDate(payDate || createdDate),
      toFormat: JALALI_DATE_TIME_FORMAT,
    }).string;

    const {
      name: statusName,
      color: statusColor,
      payMethod,
    } = getTransactionStatus({
      isPaid: item.isPaid,
      isUnfetched: false,
      isRefund,
      payMethod: ipgType,
    });

    return {
      amount: formatLocalizedNumber({ value: listItemAmount ?? 0 }),
      code: listItemCode,
      clientRefId,
      date,
      ipgType,
      key: index,
      name: ensureString({ text: name ?? "", fallbackValue: "-" }),
      payMethod,
      status: statusName,
      statusColor,
    };
  });

  const cols: ColumnsType<TransactionRowType> = [
    {
      // مبلغ
      dataIndex: "amount",
      render(value) {
        return (
          <Flex align="center">
            <Typography.Text>{value}</Typography.Text>
            <CuToman width={24} height={24} />
          </Flex>
        );
      },
      title: t("report.table.amount"),
    },
    {
      // وضعیت پرداخت
      dataIndex: "status",
      render(_, tableData) {
        const text =
          tableData.ipgType === PaymentTypeEnum.CASH
            ? `${tableData.status} (${tableData.payMethod})`
            : tableData.status;

        return (
          <Flex gap="small">
            <Badge color={tableData.statusColor} />
            <span>{text}</span>
          </Flex>
        );
      },
      title: t("report.table.status"),
    },
    {
      // پرداخت کننده
      dataIndex: "name",
      ellipsis: true,
      title: t("report.table.name"),
    },
    {
      // کد پرداخت
      className: "en-font",
      dataIndex: "code",
      render(_, tableData) {
        return (
          <Copy showIcon text={tableData.code || ""}>
            <p className="tw-m-0 tw-max-w-[110px] tw-truncate">{tableData.code}</p>
          </Copy>
        );
      },
      title: t("report.table.code"),
    },
    {
      // شناسه مرجع سرویس
      className: "en-font",
      dataIndex: "clientRefId",
      render(_, tableData) {
        return (
          <Copy showIcon text={tableData.clientRefId || ""}>
            <p className="tw-m-0 tw-max-w-[110px] tw-truncate">{tableData.clientRefId}</p>
          </Copy>
        );
      },
      title: t("report.table.referenceId"),
    },
    {
      // تاریخ پرداخت
      dataIndex: "date",
      render(value) {
        return (
          <div className={"tw-flex tw-justify-start"}>
            <div style={{ direction: "ltr" }}>{value}</div>
          </div>
        );
      },
      title: t("report.table.date"),
    },
    {
      // جزییات
      title: t("common.operations"),
      key: "action",
      render: (record) => (
        <Typography.Link
          onClick={() => {
            setDetailCode(data[record.key]?.code);
          }}
          key={data[record.key]?.code}
        >
          <TableAction label={t("common.detail")} />
        </Typography.Link>
      ),
    },
  ];

  return (
    <Flex vertical className={"tw-w-full"} gap={16}>
      {/* < تایتل صفحه: آخرین پرداخت ها - فیلتر فرم ها ( جستوجو - فیلترها - دانلود فایل ) > */}
      <TransactionFilter
        retry={retry}
        loading={loading}
        onChangeFilters={(filters) => {
          setSearchParams((pre) => ({ ...pre, ...filters }));
        }}
        filters={searchParams}
      />

      {/* < جدول تراکنش ها  > */}
      <Table
        loading={loading}
        hasIdColumn
        dataSource={data}
        size={"large"}
        columns={cols}
        pagination={{
          onChange: (pgNumber, pgSize) => {
            setSearchParams((pre) => ({
              ...pre,
              pageNumber: pgNumber,
              pageSize: pgSize,
            }));
          },
          onShowSizeChange: (_currentPage, pgSize) => {
            setSearchParams((pre) => ({
              ...pre,
              pageNumber: 1,
              pageSize: pgSize,
            }));
          },
          pageNumber: pageNumber,
          pageSize: pageSize,
          total: listCount,
        }}
      />

      {/* < دراور مربوط به جزئیات در جدول > */}
      {detailCode ? (
        <TransactionDetails
          data={transactionDetailsData}
          code={detailCode}
          open={!!detailCode}
          onClose={() => {
            setDetailCode(undefined);
          }}
        />
      ) : null}
    </Flex>
  );
};

export default ShowAllTransactionDetails;
